<!-- 代码已包含 CSS：使用 TailwindCSS , 安装 TailwindCSS 后方可看到布局样式效果 -->

<template>
    <div class="min-h-screen bg-gray-50 p-8">
      <div class="mx-auto max-w-7xl">
        <!-- 标题区域 -->
        <div class="mb-8">
          <p class="mt-2 text-gray-600">选择仓库和库位，选择产品</p>
        </div>
  
        <!-- 选择区域 -->
        <div class="mb-8 grid grid-cols-2 gap-6">
          <!-- 仓库选择 -->
          <div class="rounded-lg bg-white p-6 shadow-sm">
            <h2 class="mb-4 text-lg font-medium text-gray-900">选择仓库</h2>
            <div class="space-y-3">
              <div v-for="(warehouse, index) in warehouses" :key="index" 
                   class="flex cursor-pointer items-center rounded-lg border p-4 transition-all hover:border-blue-500"
                   :class="{'border-blue-500 bg-blue-50': selectedWarehouse === warehouse.id, 'border-gray-200': selectedWarehouse !== warehouse.id}"
                   @click="selectWarehouse(warehouse.id)">
                <i class="fa-solid fa-warehouse mr-3 text-xl" :class="{'text-blue-500': selectedWarehouse === warehouse.id, 'text-gray-400': selectedWarehouse !== warehouse.id}"></i>
                <div>
                  <p class="font-medium" :class="{'text-blue-600': selectedWarehouse === warehouse.id}">{{ warehouse.name }}</p>
                  <p class="text-sm text-gray-500">{{ warehouse.address }}</p>
                </div>
              </div>
            </div>
          </div>
  
          <!-- 库位选择 -->
          <div class="rounded-lg bg-white p-6 shadow-sm">
            <h2 class="mb-4 text-lg font-medium text-gray-900">选择库位</h2>
            <div class="space-y-3">
              <div v-for="(location, index) in filteredLocations" :key="index"
                   class="flex cursor-pointer items-center rounded-lg border p-4 transition-all hover:border-blue-500"
                   :class="{'border-blue-500 bg-blue-50': selectedLocation === location.id, 'border-gray-200': selectedLocation !== location.id}"
                   @click="selectLocation(location.id)">
                <i class="fa-solid fa-box-archive mr-3 text-xl" :class="{'text-blue-500': selectedLocation === location.id, 'text-gray-400': selectedLocation !== location.id}"></i>
                <div>
                  <p class="font-medium" :class="{'text-blue-600': selectedLocation === location.id}">{{ location.name }}</p>
                  <p class="text-sm text-gray-500">可用容量：{{ location.capacity }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- 产品列表 -->
        <div class="rounded-lg bg-white p-6 shadow-sm">
          <div class="mb-6 flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900">产品列表</h2>
            <div class="flex items-center space-x-4">
              <div class="relative">
                <input type="text" 
                       v-model="searchQuery" 
                       placeholder="搜索产品" 
                       class="w-64 rounded-lg border-none bg-gray-100 pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
              <button class="!rounded-button flex items-center space-x-2 bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 whitespace-nowrap">
                <i class="fa-solid fa-plus"></i>
                <span>批量操作</span>
              </button>
            </div>
          </div>
  
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="w-16 py-4 px-4 text-left">
                    <input type="checkbox" 
                           :checked="selectAll" 
                           @change="toggleSelectAll"
                           class="h-4 w-4 rounded border-gray-300 text-blue-600">
                  </th>
                  <th class="py-4 px-4 text-left text-sm font-medium text-gray-500">产品信息</th>
                  <th class="py-4 px-4 text-left text-sm font-medium text-gray-500">库存数量</th>
                  <th class="py-4 px-4 text-left text-sm font-medium text-gray-500">入库时间</th>
                  <th class="py-4 px-4 text-left text-sm font-medium text-gray-500">状态</th>
                  <th class="py-4 px-4 text-left text-sm font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-for="product in filteredProducts" :key="product.id" class="hover:bg-gray-50">
                  <td class="py-4 px-4">
                    <input type="checkbox" 
                           v-model="selectedProducts" 
                           :value="product.id"
                           class="h-4 w-4 rounded border-gray-300 text-blue-600">
                  </td>
                  <td class="py-4 px-4">
                    <div class="flex items-center">
                      <img :src="product.image" alt="产品图片" class="h-12 w-12 rounded-lg object-cover">
                      <div class="ml-4">
                        <div class="font-medium text-gray-900">{{ product.name }}</div>
                        <div class="text-sm text-gray-500">编号：{{ product.sku }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="py-4 px-4">
                    <span class="text-gray-900">{{ product.stock }}</span>
                  </td>
                  <td class="py-4 px-4">
                    <span class="text-gray-500">{{ product.inboundTime }}</span>
                  </td>
                  <td class="py-4 px-4">
                    <span :class="{
                      'bg-green-100 text-green-800': product.status === '正常',
                      'bg-yellow-100 text-yellow-800': product.status === '待补货',
                      'bg-red-100 text-red-800': product.status === '库存不足'
                    }" class="inline-flex rounded-full px-2 py-1 text-xs font-medium">
                      {{ product.status }}
                    </span>
                  </td>
                  <td class="py-4 px-4">
                    <div class="flex space-x-2">
                      <button class="!rounded-button whitespace-nowrap rounded px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50">
                        编辑
                      </button>
                      <button class="!rounded-button whitespace-nowrap rounded px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-50">
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- 分页 -->
          <div class="mt-6 flex items-center justify-between">
            <div class="text-sm text-gray-700">
              显示 <span class="font-medium">1</span> 到 <span class="font-medium">10</span> 条，共 <span class="font-medium">{{ products.length }}</span> 条
            </div>
            <div class="flex space-x-2">
              <button class="!rounded-button whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                上一页
              </button>
              <button class="!rounded-button whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    data() {
      return {
        warehouses: [
          { id: 1, name: '华东中心仓库', address: '上海市浦东新区张江高科技园区' },
          { id: 2, name: '华南物流中心', address: '广东省深圳市南山区科技园' },
          { id: 3, name: '华北配送中心', address: '北京市顺义区临空经济核心区' }
        ],
        locations: [
          { id: 1, warehouseId: 1, name: 'A 区-01', capacity: '80%' },
          { id: 2, warehouseId: 1, name: 'A 区-02', capacity: '65%' },
          { id: 3, warehouseId: 2, name: 'B 区-01', capacity: '45%' },
          { id: 4, warehouseId: 2, name: 'B 区-02', capacity: '90%' },
          { id: 5, warehouseId: 3, name: 'C 区-01', capacity: '70%' },
          { id: 6, warehouseId: 3, name: 'C 区-02', capacity: '55%' }
        ],
        products: [
          {
            id: 1,
            name: '高性能笔记本电脑',
            sku: 'LP-2023001',
            stock: 156,
            status: '正常',
            inboundTime: '2023-12-01 10:30',
            image: 'https://ai-public.mastergo.com/ai/img_res/ea878eb8bb023e9caa9a7b3ffa391108.jpg'
          },
          {
            id: 2,
            name: '智能手表 Pro',
            sku: 'SW-2023002',
            stock: 89,
            status: '待补货',
            inboundTime: '2023-12-02 14:20',
            image: 'https://ai-public.mastergo.com/ai/img_res/0dde4fa4d810071a210d52e010f0c53f.jpg'
          },
          {
            id: 3,
            name: '无线降噪耳机',
            sku: 'HP-2023003',
            stock: 42,
            status: '库存不足',
            inboundTime: '2023-12-03 09:15',
            image: 'https://ai-public.mastergo.com/ai/img_res/3441270e576e62bb87ea907b1f3ccb11.jpg'
          },
          {
            id: 4,
            name: '4K 超清显示器',
            sku: 'MN-2023004',
            stock: 95,
            status: '正常',
            inboundTime: '2023-12-04 16:40',
            image: 'https://ai-public.mastergo.com/ai/img_res/4a02e039cddf9d8ff353614a4ece6566.jpg'
          },
          {
            id: 5,
            name: '机械键盘',
            sku: 'KB-2023005',
            stock: 127,
            status: '正常',
            inboundTime: '2023-12-05 11:25',
            image: 'https://ai-public.mastergo.com/ai/img_res/0fa461c9a49d57a2d1808b50e9503924.jpg'
          }
        ],
        selectedWarehouse: null,
        selectedLocation: null,
        selectedProducts: [],
        searchQuery: '',
      };
    },
    computed: {
      filteredLocations() {
        if (!this.selectedWarehouse) return [];
        return this.locations.filter(location => location.warehouseId === this.selectedWarehouse);
      },
      filteredProducts() {
        return this.products.filter(product =>
          product.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          product.sku.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      },
      selectAll: {
        get() {
          return this.products.length === this.selectedProducts.length;
        },
        set(value) {
          this.selectedProducts = value ? this.products.map(p => p.id) : [];
        }
      }
    },
    methods: {
      selectWarehouse(id) {
        this.selectedWarehouse = id;
        this.selectedLocation = null;
      },
      selectLocation(id) {
        this.selectedLocation = id;
      },
      toggleSelectAll() {
        this.selectAll = !this.selectAll;
      }
    }
  };
  </script>
  
  <style scoped>
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  </style>
  
  


// WEBPACK FOOTER //
// src/views/plus/out_management/index/part/test.vue